import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const sizeButtonClass = (v, variantId) => {
  if (!v.available) return "opacity-40 line-through cursor-not-allowed bg-secondary";
  if (variantId === v.id) return "bg-ink text-paper";
  return "bg-paper hover:bg-yellow";
};

const addToCartLabel = (product, selected) => {
  if (product.sold_out) return "UNAVAILABLE";
  if (selected) return "ADD TO CART";
  return "PICK A SIZE FIRST";
};

function Gallery({ product, imageIndex, setImageIndex }) {
  return (
    <div>
      <div className="bg-white aspect-square shadow-print" data-testid="product-gallery">
        <img
          src={product.images[imageIndex]}
          alt={product.name}
          className="w-full h-full object-contain p-8"
          data-testid="product-main-image"
        />
      </div>
      {product.images.length > 1 && (
        <div className="flex gap-3 mt-4">
          {product.images.map((src, i) => (
            <button
              key={src}
              data-testid={`product-thumb-${i}`}
              onClick={() => setImageIndex(i)}
              className={`w-20 h-20 bg-white border-2 ${i === imageIndex ? "border-red" : "border-transparent"}`}
            >
              <img src={src} alt="" className="w-full h-full object-contain p-1.5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [variantId, setVariantId] = useState(null);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setImageIndex(0);
    setVariantId(null);
    api.product(slug).then(setProduct).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-6xl mx-auto px-5 py-24" data-testid="product-not-found">
        <p className="font-college text-2xl">THAT PIECE DOESN'T EXIST.</p>
        <Link to="/shop" className="text-red underline mt-4 inline-block">Back to the shop</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="max-w-6xl mx-auto px-5 py-24 text-ink/50" data-testid="product-loading">Loading…</div>;
  }

  const selected = product.variants.find((v) => v.id === variantId) || null;
  const canAdd = selected && selected.available && !product.sold_out;

  return (
    <div className="max-w-6xl mx-auto px-5 pt-10" data-testid="product-page">
      <Link to="/shop" data-testid="product-back-link" className="font-college text-base text-ink/60 hover:text-red transition-colors flex items-center gap-1">
        <ArrowLeft size={14} /> BACK TO SHOP
      </Link>

      <div className="grid gap-10 lg:grid-cols-2 mt-8">
        <Gallery product={product} imageIndex={imageIndex} setImageIndex={setImageIndex} />

        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-college text-base text-red">{product.category}</p>
            {product.seasonal_tag && (
              <span className="bg-navy text-paper font-college text-sm px-2 py-0.5" data-testid="product-seasonal-tag">
                {product.seasonal_tag}
              </span>
            )}
          </div>
          <h1 className="font-college text-3xl md:text-4xl text-navy mt-2" data-testid="product-name">{product.name}</h1>
          <p className="text-2xl font-semibold mt-3" data-testid="product-price">${product.price}</p>

          {product.sold_out && (
            <p className="mt-4 inline-block bg-red text-paper font-college text-lg px-3 py-1" data-testid="product-sold-out">
              NOT AVAILABLE RIGHT NOW
            </p>
          )}

          <p className="text-ink/70 mt-5 leading-relaxed max-w-md" data-testid="product-description">{product.description}</p>

          <div className="mt-7">
            <p className="font-college text-lg mb-3">SIZE</p>
            <div className="flex flex-wrap gap-2" data-testid="size-selector">
              {product.variants.map((v) => (
                <button
                  key={v.id}
                  data-testid={`size-${v.name}`}
                  disabled={!v.available}
                  onClick={() => setVariantId(v.id)}
                  className={`min-w-[3rem] px-3 py-2 font-semibold text-sm transition-colors ${sizeButtonClass(v, variantId)}`}
                >
                  {v.name}
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="add-to-cart-button"
            disabled={!canAdd}
            onClick={() => {
              addItem(product, selected);
              toast.success(`ADDED — ${product.name} (${selected.name})`);
            }}
            className={`mt-8 w-full sm:w-auto font-college text-xl px-10 py-3.5 transition-colors ${
              canAdd ? "bg-navy text-paper shadow-print hover:bg-red" : "bg-secondary text-ink/40 cursor-not-allowed"
            }`}
          >
            {addToCartLabel(product, selected)}
          </button>

          <ul className="mt-8 space-y-1.5 text-sm text-ink/60" data-testid="product-details">
            {product.details.map((d) => (
              <li key={d} className="flex gap-2">
                <span className="text-red">·</span> {d}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
